import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import SectionReveal from "@/components/SectionReveal";
import SEO from "@/components/SEO";
import LeadDialog from "@/components/LeadDialog";
import { SECUREAI_PLATFORMS } from "@/config/secureAiMarketing";

const capabilities = [
  ["The path out", "Guide supported AI-tool traffic through SecureAI, so the connection leaves on a controlled path."],
  ["Verified only", "Protection is shown after the path is verified. A running process is not enough."],
  ["Prompts stay private", "SecureAI does not read prompts, source code or tokens. It is not a content-inspection product."],
  ["One place", "Manage supported AI connections together, with a clear view of protection for the tools your team uses."],
];

export default function SecureAIEnterprisePage() {
  const [salesOpen, setSalesOpen] = useState(false);
  const openSales = () => setSalesOpen(true);

  return <div className="bg-white text-ink-900">
    <SEO
      title="SecureAI for Enterprise - Licensing for Your Organisation"
      description="SecureAI for teams. Protect the path compatible AI tools take, without reading prompts, code or tokens. Contact sales for enterprise licensing."
      canonicalPath="/secureai-enterprise"
      image="/secureai/official-secureai-wordmark.png"
    />

    <section className="lf-product-hero relative overflow-hidden bg-[#061122] text-white">
      <div className="absolute inset-0 lf-grid-bg-faint opacity-20" />
      <div className="lf-container relative grid items-center gap-12 py-16 md:py-24 lg:grid-cols-[1.05fr_.95fr]">
        <div>
          <div className="lf-eyebrow text-cyan-200">SecureAI for enterprise</div>
          <img src="/secureai/official-secureai-wordmark-transparent-788632a4.svg" alt="SecureAI by LightningFlow" className="mt-6 h-auto w-full max-w-[18rem]" width={600} height={152} />
          <h1 className="mt-6 max-w-3xl font-display text-5xl font-black leading-[.92] tracking-[-.04em] md:text-7xl">Protect the AI path across your team.</h1>
          <p className="mt-7 max-w-2xl text-lg leading-relaxed text-slate-200 md:text-xl">The same path protection as SecureAI, licensed for your organisation. Prompts, code and tokens stay unread.</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <button type="button" onClick={openSales} className="lf-btn border border-cyan-300 bg-cyan-300 text-ink-900 hover:border-white hover:bg-white" data-testid="secureai-enterprise-contact-sales">Contact sales <ArrowRight className="h-4 w-4" /></button>
            <Link className="lf-btn border border-white/40 text-white hover:border-white" to="/secureai">See SecureAI for Mac</Link>
          </div>
        </div>
        <img src="/secureai/official-secureai-hero-transparent-9cae3ec8.svg" alt="" className="mx-auto h-auto w-full max-w-md" width={560} height={560} data-testid="secureai-enterprise-hero-art" />
      </div>
    </section>

    <section className="lf-editorial-section border-b border-ink-100">
      <div className="lf-container">
        <div className="lf-eyebrow">What you get</div>
        <h2 className="mt-4 max-w-3xl font-display text-4xl font-black leading-tight md:text-5xl">Protection on the connection. Not on the content.</h2>
        <div className="mt-10 grid gap-px border border-ink-100 bg-ink-100 md:grid-cols-2">
          {capabilities.map(([title, body]) => <SectionReveal as="article" key={title} className="lf-feature-panel bg-white p-7 md:p-9"><h3 className="font-display text-2xl font-bold">{title}</h3><p className="mt-3 leading-relaxed text-ink-600">{body}</p></SectionReveal>)}
        </div>
      </div>
    </section>

    <section className="lf-editorial-section bg-ink-50/50">
      <div className="lf-container">
        <div className="lf-eyebrow">Coverage</div>
        <h2 className="mt-4 font-display text-4xl font-black leading-tight md:text-5xl">Honest about which tools are covered.</h2>
        <p className="mt-5 max-w-3xl text-lg leading-relaxed text-ink-600">Enterprise licensing does not widen coverage beyond what SecureAI can verify today.</p>
        <div className="mt-10 grid gap-px border border-ink-100 bg-ink-100 md:grid-cols-2">
          {SECUREAI_PLATFORMS.map((platform) => <article key={platform.name} className="bg-white p-7" data-testid={`secureai-enterprise-platform-${platform.name.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-baseline justify-between gap-4"><h3 className="font-display text-2xl font-bold">{platform.name}</h3><span className="font-mono text-[10px] uppercase tracking-[.16em] text-ink-500">{platform.status}</span></div>
            <p className="mt-3 leading-relaxed text-ink-600">{platform.body}</p>
          </article>)}
        </div>
      </div>
    </section>

    <section className="border-y border-ink-100 bg-[#061122] text-white">
      <div className="lf-container grid gap-8 py-16 md:grid-cols-2 md:py-20">
        <div>
          <div className="lf-eyebrow text-cyan-200">Privacy</div>
          <h2 className="mt-4 font-display text-4xl font-black">Your work is not the product.</h2>
        </div>
        <ul className="space-y-4 text-lg leading-relaxed text-slate-200">
          <li>Does not read prompts.</li>
          <li>Does not read source code.</li>
          <li>Does not read tokens.</li>
          <li>Does not inspect content.</li>
        </ul>
      </div>
    </section>

    <section className="lf-editorial-section" data-testid="secureai-enterprise-final">
      <div className="lf-container grid gap-8 lg:grid-cols-[1.1fr_.9fr] lg:items-end">
        <div>
          <div className="lf-eyebrow">Enterprise licensing</div>
          <h2 className="mt-4 font-display text-4xl font-black leading-tight md:text-6xl">Talk to sales.</h2>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink-600">SecureAI for the organisation is licensed with sales. There is no public price and no self-serve checkout on this page.</p>
        </div>
        <div className="lg:justify-self-end">
          <button type="button" onClick={openSales} className="lf-btn-primary" data-testid="secureai-enterprise-contact-sales-final">Contact sales <ArrowRight className="h-4 w-4" /></button>
        </div>
      </div>
    </section>

    <LeadDialog
      open={salesOpen}
      onOpenChange={setSalesOpen}
      kind="pilot"
      title="Contact sales"
      description="Tell us about enterprise licensing for SecureAI."
    />
  </div>;
}
